import { Chart } from 'chart.js';

// Initialize Chart.js defaults in a function to ensure Chart is loaded first
export function initChartDefaults() {
  // Only set defaults if Chart is properly loaded
  if (Chart && Chart.defaults) {
    Chart.defaults.font = Chart.defaults.font || {};
    Chart.defaults.font.family = "'Nunito', sans-serif";
    Chart.defaults.color = '#718096';
  }
}

// Parkbot brand colors
export const PARKBOT_COLORS = {
  primary: '#f8475f',
  secondary: '#4c51bf',
  success: '#38b2ac',
  info: '#4299e1',
  warning: '#ed8936',
  danger: '#e53e3e',
  light: '#e2e8f0',
  dark: '#2d3748',
  muted: '#718096',
  // Additional colors for charts
  colors: [
    '#f8475f', // primary
    '#4c51bf', // secondary
    '#38b2ac', // success
    '#4299e1', // info
    '#ed8936', // warning
    '#e53e3e', // danger
    '#805ad5', // purple
    '#d53f8c', // pink
    '#dd6b20', // orange
    '#3182ce', // blue
    '#2b6cb0', // dark blue
    '#2c5282', // indigo
  ]
};

// Common chart options
export const getCommonChartOptions = (title = '') => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        titleFont: {
          size: 13,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        cornerRadius: 4,
        padding: 10,
        displayColors: true
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: 16,
          weight: 'bold'
        }
      }
    }
  };
};

// Line chart configuration
export const getLineChartConfig = (labels, datasets, title = '') => {
  return {
    type: 'line',
    data: {
      labels,
      datasets
    },
    options: {
      ...getCommonChartOptions(title),
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            drawBorder: false,
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            padding: 10
          }
        },
        x: {
          grid: {
            drawBorder: false,
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            padding: 10
          }
        }
      }
    }
  };
};

// Bar chart configuration
export const getBarChartConfig = (labels, datasets, title = '') => {
  return {
    type: 'bar',
    data: {
      labels,
      datasets
    },
    options: {
      ...getCommonChartOptions(title),
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            drawBorder: false,
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            padding: 10
          }
        },
        x: {
          grid: {
            drawBorder: false,
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            padding: 10
          }
        }
      }
    }
  };
};

// Pie chart configuration
export const getPieChartConfig = (labels, datasets, title = '') => {
  return {
    type: 'pie',
    data: {
      labels,
      datasets
    },
    options: {
      ...getCommonChartOptions(title),
      cutout: '0%'
    }
  };
};

// Doughnut chart configuration
export const getDoughnutChartConfig = (labels, datasets, title = '') => {
  return {
    type: 'doughnut',
    data: {
      labels,
      datasets
    },
    options: {
      ...getCommonChartOptions(title),
      cutout: '50%'
    }
  };
};

// Horizontal bar chart configuration
export const getHorizontalBarChartConfig = (labels, datasets, title = '') => {
  return {
    type: 'bar',
    data: {
      labels,
      datasets
    },
    options: {
      ...getCommonChartOptions(title),
      indexAxis: 'y',
      scales: {
        x: {
          beginAtZero: true,
          grid: {
            drawBorder: false,
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            padding: 10
          }
        },
        y: {
          grid: {
            drawBorder: false,
            color: 'rgba(0, 0, 0, 0.05)'
          },
          ticks: {
            padding: 10
          }
        }
      }
    }
  };
};

// Format minutes to hours and minutes
export const formatMinutes = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

// Format currency
export const formatCurrency = (amount, currency = 'PHP') => {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency
  }).format(amount);
};

// Format hours to AM/PM
export function formatHour(hour: number): string {
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour} ${suffix}`;
}

// Format minutes into hours and minutes
export function formatDuration(minutes: number): string {
  if (isNaN(minutes) || minutes < 0) return '0h 0m';
  
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  
  return `${hours}h ${mins}m`;
}

// Helper functions for chart configurations
export function createBarChartConfig(labels: string[], datasets: any[], options: any = {}): any {
  return {
    type: 'bar',
    data: {
      labels,
      datasets
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: PARKBOT_COLORS.light,
            drawBorder: false,
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      },
      plugins: {
        legend: {
          position: 'bottom',
        }
      },
      ...options
    }
  };
}

export function createLineChartConfig(labels: string[], datasets: any[], options: any = {}): any {
  return {
    type: 'line',
    data: {
      labels,
      datasets
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      elements: {
        line: {
          tension: 0.3
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: PARKBOT_COLORS.light,
            drawBorder: false,
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      },
      plugins: {
        legend: {
          position: 'bottom',
        }
      },
      ...options
    }
  };
}

export function createDoughnutChartConfig(labels: string[], datasets: any[], options: any = {}): any {
  return {
    type: 'doughnut',
    data: {
      labels,
      datasets
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      plugins: {
        legend: {
          position: 'bottom',
        }
      },
      ...options
    }
  };
}

export function createHorizontalBarConfig(labels: string[], datasets: any[], options: any = {}): any {
  return {
    type: 'bar',
    data: {
      labels,
      datasets
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          beginAtZero: true,
          grid: {
            color: PARKBOT_COLORS.light,
            drawBorder: false,
          }
        },
        y: {
          grid: {
            display: false
          }
        }
      },
      plugins: {
        legend: {
          position: 'bottom',
        }
      },
      ...options
    }
  };
} 